import type { CreateCompanyRequest, KboSearchRow } from "../../api/company";

/**
 * PURE helpers for turning a `kbo` register row into the payload
 * `POST /api/v1/company` expects.
 *
 * Lifted VERBATIM in behaviour from `AddCompanyPage.tsx` so the Customer Switch's
 * inline create produces a company that is indistinguishable from one created
 * through the self-service page.
 *
 * ⚠️ `AddCompanyPage` is DELIBERATELY not refactored onto these helpers in this
 * change. It is the live self-service onboarding path, tangled with `toast.promise`
 * render props and a `window.location.reload()`, and it is not what this feature is
 * about. The duplication is intentional and tracked as a follow-up — if you change a
 * rule here, check whether `AddCompanyPage` needs the same change.
 */

/** The exact field set `CompanyForm` and `BillingForm` are written against. */
export interface CompanyDraft {
  companyName: string;
  vatNumber: string;
  addressLine1: string;
  addressLine2: string;
  addressNumber: string;
  addressNumber2: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  web: string;
  telephone: string;
  metadataNumber: string;
  metadataNumberText: string;
  metadataFormattedNumber: string;
  metadataStartDate: string;
  metadataType: string;
  metadataLegalForm: string;
  metadataJuridicalSituation: string;
  metadataNACECode: string;
  user_id: string | number;
  billingEmail: string;
  billingAddressStreet: string;
  billingAddressNumber: string;
  billingAddressZip: string;
  billingAddressCity: string;
  billingAddressCountry: string;
  invoicingCycle: string;
  iban: string;
}

/**
 * The VAT shape `AddCompanyPage` gates on before it will call the create endpoint.
 * Kept as one exported constant so the gate and its test read the same regex.
 */
export const VAT_PATTERN = /^BE\s?\d{4}\.\d{3}\.\d{3}$/;

/**
 * The zero-padded, 10-digit enterprise number — `kbo.numberText`.
 *
 * 🔴 THIS IS THE ONLY FORM SAFE TO SHOW A USER. `kbo.number` is `int unsigned`, so it
 * physically cannot hold a leading zero, and **1 621 754 of the 1 952 120 rows on dev
 * (83%) have one**. Rendering the int shows 83% of users a DIFFERENT enterprise number
 * from the one printed on their own paperwork.
 *
 * `numberText` is exactly `LPAD(number, 10, '0')` — verified against the full register:
 * 1 952 120 matches, 0 mismatches — so deriving it locally is equivalent to reading the
 * column, and is how a hand-typed number is normalised back to the printed form.
 */
export const toNumberText = (digits: string): string =>
  /^\d{1,10}$/.test(digits) ? digits.padStart(10, "0") : "";

/**
 * The register writes numbers three ways — `0404.218.004`, `BE0404218004`,
 * `404218004` — and the API keys on the leading-zero-stripped form.
 *
 * - `originalDigits` keeps the leading zero (10 digits) and is what the VAT display
 *   form is built from.
 * - `kboNumber` drops it, and IS `companies.id` / `companies.companyNumber`.
 */
export const parseRegistrationInput = (
  input: string
): { originalDigits: string; kboNumber: string; numberText: string; vatFormatted: string } => {
  let cleaned = (input ?? "").replace(/\s|\./g, "").toUpperCase();

  if (cleaned.startsWith("BE")) {
    cleaned = cleaned.slice(2);
  }

  const originalDigits = cleaned;

  if (cleaned.startsWith("0")) {
    cleaned = cleaned.slice(1);
  }
  const kboNumber = cleaned;

  let vatFormatted = "";
  if (originalDigits.length >= 10) {
    vatFormatted = `BE ${originalDigits.slice(0, 4)}.${originalDigits.slice(4, 7)}.${originalDigits.slice(7, 10)}`;
  }

  return { originalDigits, kboNumber, numberText: toNumberText(originalDigits), vatFormatted };
};

/** `"null"` really does come back as the four-character string. */
const clean = (val: string | null | undefined): string =>
  val === null || val === undefined || val === "null" ? "" : val;

/**
 * A register row → an editable draft. Mirrors `AddCompanyPage.handleSelectCompany`.
 *
 * `country` falls back to "Belgium": the only register behind this endpoint is the
 * Belgian KBO/BCE, and `AddCompanyPage` reaches the same value through its
 * `CountrySelector`, which unconditionally emits "Belgium" on mount.
 */
export const draftFromKboRow = (row: KboSearchRow): CompanyDraft => ({
  companyName: clean(row.companyName),
  vatNumber: clean(row.vatNumber),
  addressLine1: clean(row.addressLine1),
  addressLine2: clean(row.addressLine2),
  addressNumber: clean(row.addressNumber),
  addressNumber2: "",
  postalCode: clean(row.postalCode),
  city: clean(row.city),
  country: clean(row.country) || "Belgium",
  email: clean(row.contact?.email),
  web: clean(row.contact?.web),
  telephone: clean(row.contact?.telephone),
  /**
   * 🔴 IDENTITY, NOT DISPLAY — and it is the ZERO-STRIPPED int form on purpose.
   *
   * This becomes the create payload's `kbo_number`, which `createCompanyHandler` writes
   * verbatim into `companies.id`, `companies.companyNumber`,
   * `billing_information.billing_id`/`company_id` and every `company_contact.company_id`
   * — all `bigint unsigned`. It must therefore equal `kbo.number`, which is what
   * `companies.id === companyNumber === kbo.number` means and what the picker's dedupe
   * compares against. Never put `numberText` here: it would still coerce (the columns are
   * numeric) but the value the FE then reasons about would stop matching `Company.id`.
   */
  metadataNumber: row.metadata?.number != null ? String(row.metadata.number) : "",
  /** DISPLAY. Falls back to deriving the padding when the column is absent. */
  metadataNumberText:
    clean(row.metadata?.numberText) ||
    (row.metadata?.number != null ? toNumberText(String(row.metadata.number)) : ""),
  metadataFormattedNumber: clean(row.metadata?.formattedNumber),
  metadataStartDate: clean(row.metadata?.startDate),
  metadataType: clean(row.metadata?.type),
  metadataLegalForm: clean(row.metadata?.legalForm),
  metadataJuridicalSituation: clean(row.metadata?.juridicalSituation),
  metadataNACECode: clean(row.metadata?.NACECode),
  user_id: "",
  billingEmail: clean(row.contact?.email),
  billingAddressStreet: clean(row.addressLine1),
  billingAddressNumber: clean(row.addressNumber),
  billingAddressZip: clean(row.postalCode),
  billingAddressCity: clean(row.city),
  billingAddressCountry: clean(row.country) || "Belgium",
  invoicingCycle: "Monthly",
  iban: "",
});

/**
 * Copy the company address into the billing address for any billing field the user
 * has not already filled. Same rule as `AddCompanyPage.handleProceedToBilling`, and
 * it runs at the SAME moment — on the way into the billing step, not at draft time,
 * so edits made on the company step are picked up.
 */
export const withBillingDefaults = (draft: CompanyDraft): CompanyDraft => ({
  ...draft,
  billingEmail: draft.billingEmail || draft.email,
  billingAddressStreet: draft.billingAddressStreet || draft.addressLine1,
  billingAddressNumber: draft.billingAddressNumber || draft.addressNumber,
  billingAddressZip: draft.billingAddressZip || draft.postalCode,
  billingAddressCity: draft.billingAddressCity || draft.city,
  billingAddressCountry: draft.billingAddressCountry || draft.country,
  invoicingCycle: draft.invoicingCycle || "Monthly",
});

/**
 * The create payload, key-for-key as `AddCompanyPage` sends it.
 *
 * `user_id` is still sent for backward compatibility, but it is NO LONGER the
 * identity the backend links: `createCompanyHandler` derives the contact from the
 * bearer token. Sending it keeps the endpoint's existing consumer working; nothing
 * here depends on it being honoured.
 */
export const buildCreateCompanyPayload = (draft: CompanyDraft, userId: string | number): CreateCompanyRequest => ({
  kbo_number: (draft.metadataNumber || "").trim(),
  company_legal_name: (draft.companyName || "").trim(),
  company_vat: (draft.vatNumber || "").trim(),
  company_founded: (draft.metadataStartDate || "").trim(),
  company_nace_code: (draft.metadataNACECode || "").trim(),
  company_legal_form: (draft.metadataLegalForm || "").trim(),
  company_email: (draft.email || "").trim(),
  company_phone: (draft.telephone || "").trim(),
  company_website: (draft.web || "").trim(),
  company_address_street: (draft.addressLine1 || "").trim(),
  company_address_number: (draft.addressNumber || "").trim(),
  company_address_zip: (draft.postalCode || "").trim(),
  company_address_city: (draft.city || "").trim(),
  company_address_country: (draft.country || "").trim(),
  user_id: String(userId ?? "").trim(),
  billing_email: (draft.billingEmail || "").trim(),
  billing_address_street: (draft.billingAddressStreet || "").trim(),
  billing_address_number: (draft.billingAddressNumber || "").trim(),
  billing_address_zip: (draft.billingAddressZip || "").trim(),
  billing_address_city: (draft.billingAddressCity || "").trim(),
  billing_address_country: (draft.billingAddressCountry || "").trim(),
  invoicing_cycle: (draft.invoicingCycle || "").trim(),
  iban: (draft.iban || "").trim(),
});
